import $ from "jquery";
import qs from "query-string";
import Marionette from "backbone.marionette";
import AnimatedRegion from "marionette-animated-region";
import cookie from "js-cookie";
import moment from "moment-timezone";

import { heapTrack } from "lib/analytics";
import config from "../config";
import steps from "./step";
import GutCheckStep from "./app/step/gut-check";
import QuestionsStep from "./app/step/questions";
import ConfirmPhoneStep from "./app/step/step-confirm-phone";
import ShiftPreferenceStep from "./app/step/shift-preference";
import SkillsStep from "./app/step/skills";
import ReviewContainer from "./app/app-review";
import events from "../events";
import util from "../util";
import rollbar from "../rollbar";
import { handleDuplicateUser } from "./util";
import component from "../components";
import models from "../models/user";
import { ApplicationReminderModal } from "./app/reminder";

import introTmpl from "../templates/intro.jst";

const IntroView = Marionette.View.extend({
    template: introTmpl,
    className: "intro-container",
    ui: {
        end: ".intro-end",
        loader: ".loader",
    },
    events: {
        "click @ui.end": "endIntro",
    },
    onRender() {
        // sn == 0: onboarding
        // sn == 2: homepage AlertCard
        const sn = util.getQueryParam("sn");
        const returning =
            sn !== "2" && window.authUser && window.complete && !window.hasSuggestions;
        const onboarding = sn === "0" && window.positions.length > 1 && !window.hasSuggestions;
        const query = qs.parse(window.location.search);

        // Remove sn params so they aren't included in the future
        if (sn) {
            delete query.sn;
            const url = `${window.location.pathname}?${qs.stringify(query)}`;
            window.history.replaceState({}, "", url);
        }

        if ((returning || onboarding) && !query[config.RESUME_GET_PARAM_FOR_RELOAD.KEY]) {
            events.trackStep("STEP_INTRO");

            const position = window.positions.length === 1 && window.positions[0].id;
            if (position && window.activePids.indexOf(position) > -1) {
                heapTrack("Active position Intro shown", {
                    position,
                });
            }

            return;
        }

        if (sn === "0" && window.gtag) {
            window.gtag("event", "conversion", { send_to: "AW-766681090/i_uLCJTKgpQBEILAyu0C" });
        }

        this.endIntro();
    },
    endIntro() {
        this.ui.loader.show();
        this.ui.end.disable();
        this.trigger("intro:end");
    },
    reset() {
        if (this.isAttached()) {
            this.ui.loader.hide();
            this.ui.end.enable();
        }
    },
});

const AppContainer = Marionette.View.extend({
    template: false,
    bar: null,
    stepIndex: 0,
    steps: [],
    regions: {
        stepContainer: {
            selector: "#step-container",
            regionClass: AnimatedRegion,
            animation: {
                showAnimation: [
                    {
                        properties: "transition.slideRightBigIn",
                    },
                ],
                hideAnimation: [
                    {
                        properties: "transition.slideLeftBigOut",
                    },
                ],
            },
        },
        reviewContainer: {
            selector: "#review-container",
        },
        introContainer: {
            selector: "#intro-container",
        },
    },
    ui: {
        nextButton: ".next-button",
        progressBar: "#progress-bar",
        loader: ".loader",
        backBtn: ".btn-back",
        whatIs: "#what-is-link",
    },
    events: {
        "click @ui.backBtn": "onBack",
        "click @ui.whatIs": "showWhatModal",
    },
    apply() {
        this.nextStep();
    },
    showWhatModal() {
        $("#what-is-modal").modal({
            closeClass: "icon-close",
            closeText: "&times;",
            fadeDuration: 350,
            fadeDelay: 0,
        });
    },
    showLeaveModal() {
        // 4 "sections"... approx ;)
        const count = 4 - Math.min(this.stepIndex, 3);
        const msg = `${count} section${count > 1 ? "s" : ""}`;
        let $mdl;
        if (!window.complete) {
            $mdl = $("#leave-save-app-modal");
        } else {
            $mdl = $("#leave-app-modal");
        }

        $mdl.find(".count").text(msg);
        $mdl.modal({
            closeClass: "icon-close",
            closeText: "",
            fadeDuration: 350,
            fadeDelay: 0,
        });
    },
    onShowRemindModal(event) {
        event.preventDefault();

        this.showRemindModal();
    },
    showRemindModal() {
        // If they've set a resume reminder (and didn't just skip it)
        // we'll take them straight home.
        if (this.model.hasResumeReminder() && !this.model.isResumeReminderSkipped()) {
            setTimeout(() => {
                window.location = "/";
            }, 0);
            return;
        }

        new ApplicationReminderModal({
            el: "#remind-app-modal",
        }).render();
    },
    initialize() {
        this.rootPage = null;
        this.forceReview = false;
        this.model = new models.User(window.data);
        this.listenTo(this.model, "step:next", this.nextStep);
        this.listenTo(this.model, "step:save", this.saveStep);
        this.listenTo(this.model, "step:apply", this.apply);
        this.listenTo(this.model, "app:reminder", this.showRemindModal);

        const init = { model: this.model };

        this.steps = [
            new steps.SignUp(init),
            new steps.BasicInfo(init),
            new steps.Availability(init),
            new SkillsStep(init),
            new steps.History({
                ...init,
                collection: this.model.get("jobs"),
            }),
            new GutCheckStep(init),
            new steps.Promo(init),
            new QuestionsStep(init),
            new SkillsStep({
                ...init,
                followup: true,
            }),
            new GutCheckStep({
                ...init,
                followup: true,
            }),
            new ShiftPreferenceStep(init),
            // If we move this further back we'll likely need to update get_step to
            // ensure we hit if the phone_state is still unverified
            new ConfirmPhoneStep(init),
            new steps.Suggestions(init),
            new steps.Review(init),
            new component.LoadingComponent({
                title: "Pre-checking",
                subtitle: "Checking your availability against job schedules",
            }),
        ];

        this.summaryStep = new steps.Summary(init);

        let stepId = window.step || 0;
        if (stepId) {
            stepId = Number(stepId);
            if (stepId < this.steps.length) {
                this.stepIndex = stepId;
            }
        }

        const query = qs.parse(window.location.search);
        // ws-param should default to history step
        if (query[config.RESUME_GET_PARAM_FOR_RELOAD.KEY]) {
            this.stepIndex = 4;
        }

        // Dev/Stage-only means of forcing a step. i.e. &_step=4 would force to work history
        if (__DEV__) {
            if (query._step) {
                this.stepIndex = Number(query._step);
            } else if (query._review) {
                this.forceReview = true;
            }
        }
    },
    loadApplication() {
        this.getRegion("introContainer").$el.hide();
        this.removeRegion("introContainer");

        // catch save and exit in the nav bar
        $("body").on("click", ".js-app-save-and-exit", this.onShowRemindModal.bind(this));

        // Since we're loading the application, listen for
        // back navigation and intercept the user to confirm
        const self = this;
        const skipSteps = ["STEP_SIGNUP", "STEP_SUGGESTIONS", "STEP_REVIEW"];

        const query = qs.parse(window.location.search, { sort: false });
        [config.RESUME_GET_PARAM_FOR_RELOAD.KEY].forEach((key) => {
            delete query[key];
        });

        // @TODO need to update to have sort work?
        const newQuery = qs.stringify(query, { sort: false });

        window.history.replaceState(
            { _app: 1 },
            "",
            `${window.location.origin}${window.location.pathname}?${newQuery}`,
        );
        window.onpopstate = (e) => {
            if (skipSteps.indexOf(self.currentStep) > -1) {
                // This step manipulates history somehow, so best to leave it alone
                return;
            }
            if (e.state && "_app" in e.state) {
                window.history.back();
                return;
            }
            self.showLeaveModal();
        };

        if (this.forceReview) {
            this.stepIndex = this.steps.length - 2;
            this.nextStep();
            return;
        }

        this.progressStep();
    },
    progressStep(resp) {
        let step;
        const positionData = this.positionData || window.positions;
        let shouldSkip = false;
        do {
            step = this.steps[this.stepIndex];

            if (step.setPositions && positionData) {
                step.setPositions(positionData);
            }

            shouldSkip = step.shouldSkip();
            if (shouldSkip) {
                this.stepIndex += 1;
            }
        } while (shouldSkip);

        if (this.stepIndex > 0) {
            $("#save-exit-link").fadeIn();
        }

        this.showChildView("stepContainer", step);

        if (step.name) {
            this.currentStep = step.name;
            events.trackStep(step.name);
        }

        if (this.stepIndex === this.steps.length - 1) {
            // Loading (submission animation..)
            $("#save-exit-link").fadeOut();
            this.ui.nextButton.hide();
            const stepRegion = this.getRegion("stepContainer");
            stepRegion.$el.css({ backgroundColor: "transparent" });
            $(".application").removeClass("review-step");

            // loadApplication set this for the duration of the app, time to remove
            window.onpopstate = null;
            this.sendCompletionTracking();

            const self = this;
            setTimeout(
                () => {
                    self.loadReview(resp);
                },
                this.forceReview ? 0 : 2800,
            );
        }

        this.checkReviewStep(step);
    },
    checkReviewStep(step) {
        if (step.name === "STEP_REVIEW") {
            this.rootPage = step.name;
            this.listenTo(step, "step:load", this.loadStepFromReview);
            this.listenTo(this.model, "step:end", this.loadRoot);
        }
    },
    loadReview(data) {
        $("#progress-bar").hide();
        $(".next-container").hide();

        const stepRegion = this.getRegion("stepContainer");
        if (stepRegion) {
            if (stepRegion.$el) {
                stepRegion.$el.hide();
            }
            stepRegion.empty();
        }
        this.showChildView(
            "reviewContainer",
            new ReviewContainer({
                data,
                user: this.model,
            }),
        );
    },
    findStepIndex(name) {
        return this.steps.reduce((memo, s, idx) => (s.name === name && !memo ? idx : memo), 0);
    },
    loadStep(name, isReview) {
        this.getRegion("stepContainer").empty();
        const opts = {};
        let step;

        if (name === "STEP_SUMMARY") {
            step = this.summaryStep;
        } else {
            step = this.steps.reduce((memo, s) => (s.name === name && !memo ? s : memo), null);
        }

        if (!step) {
            rollbar.warn(`Failed to load step ${name}`);
            return;
        }

        const { options } = step;
        options.isProfile = true;
        options.isReview = !!isReview;
        const view = new step.constructor({
            ...options,
            ...opts,
        });

        const positionData = this.positionData || window.positions;
        if (view.setPositions && positionData) {
            view.setPositions(positionData);
        }

        this.currentStep = view.name;

        if (!isReview) {
            events.track("PROFILE_VIEW", view.name);
        }

        const self = this;
        setTimeout(() => {
            self.showChildView("stepContainer", view);
        }, 300);
    },

    /**
     * @param {string} step - passed as AJAX param so the server knows where we are
     */
    saveApplication(data, step, deferred) {
        step = step || this.stepIndex;

        // If we're in the middle of a request, just save the next request for later.
        // We don't need real "queueing" here because app/start is very wise and
        // sends the entire payload on every request. Very wise. So we just need
        // to take in whatever the latest request is.
        if (this.savingApplication) {
            this.nextRequest = {
                data,
                step,
                deferred: this.nextRequest ? this.nextRequest.deferred : $.Deferred(),
            };

            return this.nextRequest.deferred;
        }

        this.savingApplication = true;

        return $.ajax({
            type: "POST",
            url: `${window.location.pathname}?_st=${step}`,
            data,
            dataType: "json",
            contentType: "application/json",
        })
            .done((...args) => {
                if (deferred) {
                    deferred.resolve(...args);
                }
            })
            .fail((...args) => {
                if (deferred) {
                    deferred.reject(...args);
                }
            })
            .always(() => {
                this.savingApplication = false;

                if (this.nextRequest) {
                    const {
                        data: nextData,
                        step: nextStep,
                        deferred: nextDeferred,
                    } = this.nextRequest;
                    this.nextRequest = null;
                    this.saveApplication(nextData, nextStep, nextDeferred);
                }
            });
    },

    saveStep() {
        // add 'mid' suffix to step tracking when saving outside nextStep
        const step = this.getRegion("introContainer") ? "intro" : `${this.stepIndex}-mid`;

        const data = this.rootPage ? JSON.stringify(this.model.toJSON()) : this.getDataString();

        return this.saveApplication(data, step)
            .done(() => {
                this.model.trigger("save:success");
            })
            .fail(() => {
                this.model.trigger("save:fail");
                this.showConnectionAlert();
            })
            .always(() => {
                this.model.trigger("save:complete");
            });
    },
    saveProfile() {
        // add 'mid' suffix to step tracking when saving outside nextStep
        const step = `${this.stepIndex}-mid`;
        const data = this.model.toJSON();

        const ids = this.getDataStringPositions();
        if (ids) {
            data.positions = ids;
        }

        return this.saveApplication(JSON.stringify(data), step)
            .done(({ positions }) => {
                this.positionData = positions;
            })
            .fail(() => {
                this.showConnectionAlert();
            });
    },
    onBack() {
        this.loadRoot();
    },
    loadRoot(save) {
        // const isReview = this.rootPage === 'STEP_REVIEW';

        if (save) {
            this.saveProfile().done(() => {
                this.loadRoot(null);
            });
            return;
        }

        this.loadReviewStep(save === false);
    },
    loadReviewStep(isBack) {
        const step = new steps.Review({
            noFetch: !!isBack,
            model: this.model,
        });
        this.currentStep = null;
        this.showChildView("stepContainer", step);
        this.listenTo(step, "step:load", this.loadStepFromReview);
    },
    loadStepFromReview(name) {
        this.loadStep(name, true);
    },
    onRender() {
        const intro = new IntroView({ stepIndex: this.stepIndex });
        this.listenTo(intro, "intro:end", () => {
            if (window.complete && !window.hasSuggestions) {
                // Create new apps for "basket" of positions
                this.saveStep()
                    .done(() => {
                        this.loadApplication();
                    })
                    .always(() => {
                        intro.reset();
                        events.trackNewApp();
                    });
            } else {
                this.loadApplication();
            }
        });
        this.showChildView("introContainer", intro);
    },
    getDataStringPositions() {
        const ids = util.getQueryParam("p");

        if (ids && ids.length > 0) {
            return ids;
        }

        return null;
    },
    getDataString() {
        const data = this.model.toJSON();
        const ids = this.getDataStringPositions();
        if (ids) {
            data.positions = ids;
        }

        const search = cookie.get("search");
        if (search && search.length > 0) {
            data.search = search;
        }

        const utm = cookie.get("utm");
        if (utm && utm.length > 0) {
            data.utm = utm;
        }

        // set clickcast ccuid in the data if exists
        // confirm source is from clickcast campaign
        const clickcastSource = util.getQueryParam("ccast-source");
        const clickcastClickID = util.getQueryParam("ccuid");
        if (clickcastSource && clickcastClickID) {
            data.clickcast_click_id = clickcastClickID;
            data.user_agent = navigator.userAgent;
        }

        data.timezone = moment.tz.guess();

        window.applicationData = data;
        if ("phone" in data && "email" in data) {
            window.user = {
                phone: data.phone,
                email: data.email,
            };
            if ("first_name" in data) {
                window.user.firstName = data.first_name;
            }
        }

        return JSON.stringify(data);
    },
    sendCompletionTracking() {
        if (!window.complete) {
            events.sendConversion("dWmeCMn6428Q8PDVnwM"); // AdWords: Registration
            events.track("FUNNEL", "PROFILE_COMPLETE");
            if (window.fbq) {
                window.fbq("track", "CompleteRegistration");
            }
        }

        // Since we don't have this step anymore..
        events.trackStep("STEP_SUCCESS");
        events.sendConversion("zmpmCP6wooUBEPDw1Z8D"); // AdWords: Application
        if (window.fbq) {
            window.fbq("track", "AddToCart");
        }
    },
    showConnectionAlert() {
        setTimeout(() => {
            /* eslint-disable no-alert */
            alert(
                "The application could not be submitted. Please check " +
                    "your internet connection and try again.",
            );
            /* eslint-enable no-alert */
        }, 150);
    },
    nextStep(resp) {
        // First, check submission logic
        if (!this.ui.nextButton.hasClass("loading")) {
            this.ui.nextButton.loading(true);

            this.saveApplication(this.getDataString())
                .done((data) => {
                    this.positionData = data.positions;
                    this.nextStep(data.positions);
                })
                .fail((data) => {
                    this.ui.nextButton.loading(false);
                    if (!handleDuplicateUser(data)) {
                        this.showConnectionAlert();
                    }

                    const step = this.steps[this.stepIndex];
                    $(".next-button").bind("click.submit", () => {
                        if (step.submit()) {
                            $(".next-button").unbind("click.submit");
                            this.nextStep();
                        }
                    });
                });
            return;
        }

        // If the position data returned by the server contains more
        // entries than the app is currently aware of, the user is using
        // an old window and we have to reload to re-sync with their updates.
        // This is also a mechanism by which SuggestionsStep.finishStep can
        // force the app to get new `questions` payload from sp positions
        if (resp && resp.length && resp.length > window.positions.length) {
            window.location.reload();
            return;
        }

        // If the user just signed up, we should identify them for the rest
        // of the app flow
        const userId = cookie.get("_ws_heap");
        if (userId && window.heap && !window.heap.identity) {
            window.heap.identify(userId);
        }

        // If a new app was created, we'll fire the necessary pixels
        events.trackNewApp();

        this.ui.nextButton.loading(false);
        // Then progress the step
        if (this.stepIndex < this.steps.length - 1) {
            window.scrollTo(0, 0);

            const prevStep = this.steps[this.stepIndex];
            if (prevStep.name === "STEP_SIGNUP" && !window.authUser) {
                if (window.fbq) window.fbq("track", "Lead");
                events.track("FUNNEL", "LEAD");
                events.sendConversion("rwQ9CK-3s3QQ8PDVnwM"); // AdWords: Lead

                if (window.gtag && window.positions && window.positions.length > 0) {
                    window.gtag("event", "checkout_progress", {
                        job_id: window.positions.map((o) => o.id.toString()),
                        job_pagetype: "conversionintent",
                    });
                }
            }

            this.stepIndex += 1;
            this.progressStep(resp);
        }
    },
});

export default AppContainer;
